/* ChooChooMist theme layer: bundle picker + sticky bar, delivery dates, gallery, mini reviews,
   sliders, videos, dialogs, shipping-protection opt-in, gift note, snowfall.
   The cart drawer is ShrinePro's: refreshed through its public renderContents()/open() API. */
(() => {
  'use strict';

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ───────── Money ───────── */
  function formatMoney(cents, format) {
    const fmt = format || '${{amount}}';
    const value = Number(cents) || 0;
    const delim = (n, decimals, thousands, decimal) => {
      const [int, frac] = (n / 100).toFixed(decimals).split('.');
      const i = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? i + decimal + frac : i;
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return delim(value, 0, ',', '.');
        case 'amount_with_comma_separator': return delim(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return delim(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return delim(value, 2, "'", '.');
        default: return delim(value, 2, ',', '.');
      }
    });
  }

  /* ───────── Cart drawer bridge (ShrinePro) ───────── */
  const getDrawer = () => document.querySelector('cart-drawer');

  async function addToCart(items) {
    const drawer = getDrawer();
    const body = { items };
    if (drawer) {
      body.sections = 'cart-drawer,cart-icon-bubble';
      body.sections_url = window.location.pathname;
    }
    const res = await fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await res.json();
    if (!res.ok || data.status) {
      const err = new Error(data.description || data.message || 'Add to cart failed');
      err.userMessage = data.description;
      throw err;
    }
    return data;
  }

  function showCart(data) {
    const drawer = getDrawer();
    if (drawer && typeof drawer.renderContents === 'function' && data.sections) {
      drawer.classList.remove('is-empty');
      drawer.renderContents(data);
      window.setTimeout(() => {
        if (!drawer.classList.contains('active') && typeof drawer.open === 'function') drawer.open();
      }, 60);
    } else {
      window.location.href = root + 'cart';
    }
  }

  /* ───────── Business-day delivery dates ───────── */
  function addBusinessDays(date, days) {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    let added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      const wd = d.getDay();
      if (wd !== 0 && wd !== 6) added += 1;
    }
    return d;
  }
  const fmtDay = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
  const fmtDayOnly = new Intl.DateTimeFormat('en-US', { day: 'numeric' });
  const range = (a, b) => {
    if (a.getTime() === b.getTime()) return fmtDay.format(a);
    if (a.getMonth() === b.getMonth()) return `${fmtDay.format(a)}–${fmtDayOnly.format(b)}`;
    return `${fmtDay.format(a)} – ${fmtDay.format(b)}`;
  };

  class JmDelivery extends HTMLElement {
    connectedCallback() {
      if (this.dataset.dates !== 'true') return;
      const n = (k) => Math.max(0, parseInt(this.dataset[k], 10) || 0);
      const pMin = n('procMin');
      const pMax = Math.max(pMin, n('procMax'));
      const dMin = n('delMin');
      const dMax = Math.max(dMin, n('delMax'));
      const today = new Date();
      const set = (key, text) => this.querySelectorAll(`[data-jm-date="${key}"]`).forEach((el) => { el.textContent = text; });
      set('ordered', fmtDay.format(today));
      set('shipped', range(addBusinessDays(today, pMin), addBusinessDays(today, pMax)));
      set('delivered', range(addBusinessDays(today, dMin), addBusinessDays(today, dMax)));
    }
  }
  if (!customElements.get('jm-delivery')) customElements.define('jm-delivery', JmDelivery);

  /* ───────── Christmas countdown (real cutoff from Theme settings) ───────── */
  const parseDeadline = (v) => {
    const t = Date.parse(v || '');
    return Number.isNaN(t) ? null : t;
  };
  const pad = (n) => String(n).padStart(2, '0');
  function hideCutoffOnly() {
    const deadline = parseDeadline(document.body.dataset.jmDeadline);
    if (deadline && Date.now() >= deadline) {
      document.querySelectorAll('[data-jm-cutoff-only]').forEach((el) => { el.hidden = true; el.style.display = 'none'; });
      document.querySelectorAll('[data-jm-after-cutoff]').forEach((el) => { el.hidden = false; });
    }
  }
  class JmCountdown extends HTMLElement {
    connectedCallback() {
      this.deadline = parseDeadline(this.dataset.deadline);
      this.threshold = (Number(this.dataset.threshold) || 7) * 864e5;
      this.stat = this.querySelector('[data-jm-cd-static]');
      this.time = this.querySelector('[data-jm-cd-time]');
      this.live = this.querySelector('[data-jm-cd-live]');
      this.done = this.querySelector('[data-jm-cd-done]');
      if (!this.deadline) return;
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    show(which) {
      if (this.stat) this.stat.hidden = which !== 'static';
      if (this.live) this.live.hidden = which !== 'live';
      if (this.done) this.done.hidden = which !== 'done';
      this.classList.toggle('is-over', which === 'done');
    }
    tick() {
      const left = this.deadline - Date.now();
      if (left <= 0) {
        clearInterval(this.timer);
        this.show('done');
        hideCutoffOnly();
        return;
      }
      if (left > this.threshold) { this.show('static'); return; }
      const sec = Math.floor(left / 1000);
      const d = Math.floor(sec / 86400);
      const h = Math.floor((sec % 86400) / 3600);
      const m = Math.floor((sec % 3600) / 60);
      const txt = left < 864e5 ? `${pad(h)}h ${pad(m)}m ${pad(sec % 60)}s` : `${d}d ${pad(h)}h ${pad(m)}m`;
      if (this.time && this.time.textContent !== txt) this.time.textContent = txt;
      this.show('live');
    }
  }
  if (!customElements.get('jm-countdown')) customElements.define('jm-countdown', JmCountdown);

  /* ───────── Daily delivery estimate: countdown to midnight US Eastern ───────── */
  const etParts = (date) => {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric', hourCycle: 'h23' }).formatToParts(date);
    const o = {};
    parts.forEach((x) => { o[x.type] = Number(x.value); });
    return o;
  };
  class JmEta extends HTMLElement {
    connectedCallback() {
      this.out = this.querySelector('[data-jm-eta-text]');
      this.tpl = this.dataset.template || '';
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    tick() {
      const now = new Date();
      const et = etParts(now);
      const secs = 86400 - (et.hour * 3600 + et.minute * 60 + et.second);
      const today = new Date(et.year, et.month - 1, et.day);
      const dMin = Math.max(0, parseInt(this.dataset.delMin, 10) || 0);
      const dMax = Math.max(dMin, parseInt(this.dataset.delMax, 10) || 0);
      const a = addBusinessDays(today, dMin);
      const b = addBusinessDays(today, dMax);
      const h = Math.floor(secs / 3600);
      const m = Math.floor((secs % 3600) / 60);
      const cd = h > 0 ? `${h}h ${pad(m)}m` : `${m}m ${pad(secs % 60)}s`;
      const txt = this.tpl
        .replace('[countdown]', cd)
        .replace('[dates]', `${fmtDay.format(a)} – ${fmtDay.format(b)}`)
        .replace('[date1]', fmtDay.format(a))
        .replace('[date2]', fmtDay.format(b));
      if (this.out && this.out.textContent !== txt) this.out.textContent = txt;
    }
  }
  if (!customElements.get('jm-eta')) customElements.define('jm-eta', JmEta);

  /* ───────── Bundle picker ───────── */
  class JmBundle extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      try {
        this.variants = JSON.parse(this.querySelector('[data-jm-variants]').textContent);
      } catch (e) {
        this.variants = [];
      }
      this.byId = new Map(this.variants.map((v) => [v.id, v]));
      this.form = this.querySelector('form');
      this.tiers = Array.from(this.querySelectorAll('[data-jm-tier]'));
      this.atc = this.querySelector('[data-jm-atc]');
      this.atcLabel = this.querySelector('[data-jm-atc-label]');
      this.idInput = this.querySelector('form input[name="id"]');
      this.qtyInput = this.querySelector('[data-jm-qty]');
      this.dynamic = this.querySelector('[data-jm-dynamic]');
      this.error = this.querySelector('[data-jm-error]');
      this.pdp = this.closest('[data-jm-pdp]');
      this.sticky = this.pdp && this.pdp.querySelector('[data-jm-sticky]');

      this.addEventListener('change', (e) => {
        const t = e.target;
        if (t.classList.contains('jm-tier__radio')) {
          this.selectTier(t.closest('[data-jm-tier]'));
        } else if (t.matches('[data-jm-item]')) {
          this.update();
          if (t.tagName === 'SELECT') this.syncGallery(Number(t.value));
        }
      });
      if (this.form) this.form.addEventListener('submit', (e) => this.submit(e));
      this.initSticky();
      this.update();
    }

    current() {
      return this.tiers.find((t) => t.querySelector('.jm-tier__radio').checked) || this.tiers[0];
    }

    itemsOf(tier) {
      return Array.from(tier.querySelectorAll('[data-jm-item]')).map((el) => Number(el.value));
    }

    selectTier(tier) {
      const prev = this._last || this.tiers.find((t) => t.classList.contains('is-selected'));
      if (prev && prev !== tier) {
        const prevVals = this.itemsOf(prev);
        tier.querySelectorAll('select[data-jm-item]').forEach((sel, i) => {
          const want = prevVals[i] !== undefined ? prevVals[i] : prevVals[0];
          if (want && sel.querySelector(`option[value="${want}"]:not([disabled])`)) sel.value = String(want);
        });
      }
      this.tiers.forEach((t) => t.classList.toggle('is-selected', t === tier));
      this._last = tier;
      this.update();
    }

    priceFor(tier) {
      let sum = 0;
      let base = 0;
      let available = true;
      const items = this.itemsOf(tier);
      items.forEach((id) => {
        const v = this.byId.get(id);
        if (!v) { available = false; return; }
        sum += v.price;
        base += Math.max(v.price, v.compare || 0);
        if (!v.available) available = false;
      });
      const off = Number(tier.dataset.off) || 0;
      const final = Math.round((sum * (100 - off)) / 100);
      // "% OFF" = real saving vs regular (or compare-at) prices, rounded down
      const pct = base > final ? Math.floor(((base - final) * 100) / base + 1e-9) : 0;
      return { final, base, pct, available, qty: items.length, same: new Set(items).size === 1 };
    }

    update() {
      const offLabel = this.dataset.offLabel || 'OFF';
      const money = (c) => formatMoney(c, this.dataset.moneyFormat);
      this.tiers.forEach((t) => {
        const r = this.priceFor(t);
        const price = t.querySelector('[data-jm-price]');
        const compare = t.querySelector('[data-jm-compare]');
        const pill = t.querySelector('[data-jm-off]');
        if (price) price.textContent = money(r.final);
        if (compare) {
          compare.textContent = money(r.base);
          compare.classList.toggle('is-hidden', r.pct <= 0);
        }
        if (pill) {
          pill.textContent = `${r.pct}% ${offLabel}`;
          pill.classList.toggle('is-hidden', r.pct <= 0);
        }
        const each = t.querySelector('[data-jm-each]');
        if (each && r.qty > 1) {
          const label = each.dataset.jmEach || '';
          each.textContent = `${money(Math.round(r.final / r.qty))} ${label}`;
        }
      });
      this.querySelectorAll('[data-jm-dot]').forEach((dot) => { // jmDot
        const sel = dot.parentElement.querySelector('[data-jm-item]');
        const v = sel && this.byId.get(Number(sel.value));
        dot.style.background = (v && v.swatch) || 'transparent';
        dot.hidden = !(v && v.swatch);
      });
      const tier = this.current();
      const cur = this.priceFor(tier);
      const row = this.pdp && this.pdp.querySelector('[data-jm-price-row]');
      if (row) {
        row.querySelector('[data-jm-row-price]').textContent = money(cur.final);
        const was = row.querySelector('[data-jm-row-compare]');
        const save = row.querySelector('[data-jm-row-save]');
        was.textContent = money(cur.base);
        was.classList.toggle('is-hidden', cur.pct <= 0);
        if (!save.dataset.tpl) save.dataset.tpl = save.textContent.replace(/\d+/, '[percent]');
        save.textContent = save.dataset.tpl.replace('[percent]', cur.pct);
        save.classList.toggle('is-hidden', cur.pct <= 0);
      }
      const label = cur.available
        ? this.dataset.atcLabel.replace('[qty]', cur.qty).replace('[total]', money(cur.final))
        : this.dataset.soldoutLabel;
      if (this.atc) this.atc.disabled = !cur.available;
      if (this.atcLabel) this.atcLabel.textContent = label;
      const firstId = this.itemsOf(tier)[0];
      if (this.idInput && firstId) this.idInput.value = firstId;
      if (this.qtyInput) this.qtyInput.value = cur.qty;
      if (this.dynamic) this.dynamic.hidden = !cur.same || !cur.available;
      if (this.sticky) {
        const names = this.itemsOf(tier).map((id) => (this.byId.get(id) || {}).title).filter((t) => t && t !== 'Default Title');
        const meta = tier.dataset.title || '';
        this.sticky.querySelector('[data-jm-sticky-meta]').textContent = meta;
        this.sticky.querySelector('[data-jm-sticky-label]').textContent = cur.available ? this.dataset.stickyLabel.replace('[qty]', cur.qty) : this.dataset.soldoutLabel;
        this.sticky.querySelector('[data-jm-sticky-price]').textContent = cur.available ? money(cur.final) : '';
        this.sticky.querySelector('[data-jm-sticky-btn]').disabled = !cur.available;
      }
    }

    initSticky() {
      if (!this.sticky || !this.atc || !('IntersectionObserver' in window)) return;
      const btn = this.sticky.querySelector('[data-jm-sticky-btn]');
      btn.addEventListener('click', () => {
        if (this.form.requestSubmit) this.form.requestSubmit();
        else this.form.dispatchEvent(new Event('submit', { cancelable: true }));
      });
      const footer = document.querySelector('.jm-footer');
      let pastAtc = false;
      let footerVisible = false;
      const render = () => {
        const show = pastAtc && !footerVisible;
        this.sticky.classList.toggle('is-visible', show);
        this.sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
        btn.tabIndex = show ? 0 : -1;
        document.body.classList.toggle('jm-has-sticky', show);
      };
      new IntersectionObserver(([en]) => {
        pastAtc = !en.isIntersecting && en.boundingClientRect.top < 0;
        render();
      }).observe(this.atc);
      if (footer) {
        new IntersectionObserver(([en]) => { footerVisible = en.isIntersecting; render(); }).observe(footer);
      }
    }

    syncGallery(variantId) {
      const v = this.byId.get(variantId);
      const gallery = this.pdp && this.pdp.querySelector('[data-jm-gallery]');
      if (!v || !gallery) return;
      if (gallery.jmFilter) gallery.jmFilter(v.title);
    }

    async submit(e) {
      e.preventDefault();
      const tier = this.current();
      const counts = new Map();
      this.itemsOf(tier).forEach((id) => counts.set(id, (counts.get(id) || 0) + 1));
      const items = Array.from(counts, ([id, quantity]) => ({ id, quantity }));
      if (!items.length) return;
      this.error.hidden = true;
      this.atc.disabled = true;
      this.atcLabel.textContent = this.dataset.addingLabel;
      try {
        showCart(await addToCart(items));
      } catch (err) {
        this.error.textContent = err.userMessage || this.dataset.errorLabel;
        this.error.hidden = false;
      } finally {
        this.update();
      }
    }
  }
  if (!customElements.get('jm-bundle')) customElements.define('jm-bundle', JmBundle);

  /* ───────── #anchor opens the matching accordion (e.g. header Shipping → #shipping) ───────── */
  function jmOpenHash() {
    const id = decodeURIComponent(location.hash.slice(1));
    if (!id) return;
    const el = document.getElementById(id);
    if (!el || el.tagName !== 'DETAILS') return;
    el.open = true;
    requestAnimationFrame(() => el.scrollIntoView({ block: 'start', behavior: reduceMotion.matches ? 'auto' : 'smooth' }));
  }
  window.addEventListener('hashchange', jmOpenHash);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', jmOpenHash);
  else jmOpenHash();

  /* ───────── Track order: open the carrier tracking site in a new tab ───────── */
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-jm-track-form]');
    if (!form) return;
    e.preventDefault();
    const input = form.querySelector('input[name="tracking"]');
    const err = form.querySelector('[data-jm-track-error]');
    const num = (input.value || '').replace(/\s+/g, '').trim();
    if (!num) { if (err) err.hidden = false; input.focus(); return; }
    if (err) err.hidden = true;
    const url = (form.dataset.url || 'https://t.17track.net/en#nums={number}').replace('{number}', encodeURIComponent(num));
    window.open(url, '_blank', 'noopener');
  });

  /* ───────── Gallery: swipe track, counter, thumbs ───────── */
  function initGallery(gallery) {
    if (gallery._jm) return;
    gallery._jm = true;
    const track = gallery.querySelector('[data-jm-gallery-track]');
    const count = gallery.querySelector('[data-jm-count]');
    const thumbs = Array.from(gallery.querySelectorAll('[data-jm-thumb]'));
    const slides = Array.from(track.children);
    const colors = (gallery.dataset.jmColors || '').split('|').map((c) => c.trim()).filter(Boolean);
    // A slide's color comes from the server (alt text, else file name); '' = no color.
    slides.forEach((s) => { s._jmColor = s.dataset.color || ''; });
    // Visual order: color photos first, then photos without a color (CSS order: 1).
    const ordered = () => {
      const vis = slides.filter((s) => !s.hidden);
      return vis.filter((s) => s.style.order !== '1').concat(vis.filter((s) => s.style.order === '1'));
    };
    let visible = ordered();
    gallery._jmColor = gallery.dataset.jmColor || '';
    const mark = (i) => {
      const active = visible[i];
      thumbs.forEach((b, n) => (slides[n] === active ? b.setAttribute('aria-current', 'true') : b.removeAttribute('aria-current')));
      if (count) count.textContent = `${i + 1} / ${Math.max(1, visible.length)}`;
    };
    const go = (s, smooth = true) => {
      if (s) track.scrollTo({ left: s.offsetLeft - track.offsetLeft, behavior: smooth && !reduceMotion.matches ? 'smooth' : 'auto' });
    };
    thumbs.forEach((b) => b.addEventListener('click', () => go(slides[Number(b.dataset.jmThumb)])));
    let raf;
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => mark(Math.round(track.scrollLeft / Math.max(1, track.clientWidth))));
    }, { passive: true });
    gallery.jmGoToMedia = (mediaId) => {
      const s = slides.find((el) => Number(el.dataset.mediaId) === Number(mediaId));
      if (s && !s.hidden) go(s);
    };
    // Show one color's photos (+ photos without a color, after them) and jump to the first.
    // A color with no photos shows everything.
    gallery.jmFilter = (color) => {
      if (!colors.length) return;
      const c = (color || '').toLowerCase().trim();
      const tagged = c && slides.some((s) => s._jmColor === c);
      slides.forEach((s, n) => {
        s.hidden = tagged ? !(s._jmColor === c || s._jmColor === '') : false;
        s.style.order = tagged && s._jmColor === '' ? '1' : '';
        if (thumbs[n]) {
          thumbs[n].hidden = s.hidden;
          thumbs[n].style.order = s.style.order;
        }
      });
      gallery._jmColor = c;
      visible = ordered();
      go(visible[0], false);
      mark(0);
    };
  }

  /* ───────── Mini review carousel (fade, dots, swipe) ───────── */
  function initMinirev(box) {
    if (box._jm) return;
    box._jm = true;
    const items = Array.from(box.querySelectorAll('[data-jm-minirev-item]'));
    const dots = Array.from(box.querySelectorAll('[data-jm-minirev-dot]'));
    if (items.length < 2) return;
    let i = 0;
    let timer;
    let paused = false;
    ['mouseenter', 'touchstart'].forEach((ev) => box.addEventListener(ev, () => { paused = true; clearInterval(timer); }, { passive: true }));
    box.addEventListener('mouseleave', () => { paused = false; auto(); });
    const show = (n) => {
      i = (n + items.length) % items.length;
      items.forEach((el, k) => el.classList.toggle('is-active', k === i));
      dots.forEach((d, k) => (k === i ? d.setAttribute('aria-current', 'true') : d.removeAttribute('aria-current')));
    };
    const auto = () => {
      clearInterval(timer);
      if (!reduceMotion.matches && !paused) timer = setInterval(() => show(i + 1), Number(box.dataset.interval) || 2500);
    };
    dots.forEach((d, k) => d.addEventListener('click', () => { show(k); auto(); }));
    let x0 = null;
    box.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
    box.addEventListener('touchend', (e) => {
      if (x0 === null) return;
      const dx = e.changedTouches[0].clientX - x0;
      if (Math.abs(dx) > 40) { show(i + (dx < 0 ? 1 : -1)); auto(); }
      x0 = null;
    });
    auto();
  }

  /* ───────── Sliders with prev/next ───────── */
  function initSlider(slider) {
    if (slider._jm) return;
    slider._jm = true;
    const track = slider.querySelector('[data-jm-slider-track]');
    const prev = slider.querySelector('[data-jm-prev]');
    const next = slider.querySelector('[data-jm-next]');
    if (!track) return;
    const step = () => {
      const first = track.children[0];
      return first ? first.getBoundingClientRect().width + 18 : track.clientWidth;
    };
    const state = () => {
      const max = track.scrollWidth - track.clientWidth - 2;
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max;
      slider.classList.toggle('is-static', max <= 0);
    };
    if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    if (next) next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
    track.addEventListener('scroll', state, { passive: true });
    window.addEventListener('resize', state, { passive: true });
    state();
  }

  /* ───────── Autoplay videos (also with reduced motion) ───────── */
  let videoObserver;
  const pendingTap = new Set();
  function tryPlay(v) {
    const p = v.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => { pendingTap.add(v); armTap(); });
    }
  }
  function armTap() {
    if (armTap.armed) return;
    armTap.armed = true;
    const kick = () => {
      pendingTap.forEach((v) => v.play().catch(() => {}));
      pendingTap.clear();
      armTap.armed = false;
      ['touchstart', 'click', 'scroll'].forEach((ev) => window.removeEventListener(ev, kick, true));
    };
    ['touchstart', 'click', 'scroll'].forEach((ev) => window.addEventListener(ev, kick, { capture: true, passive: true }));
  }
  function initVideos(scope) {
    if (!videoObserver && 'IntersectionObserver' in window) {
      videoObserver = new IntersectionObserver((entries) => {
        entries.forEach((en) => (en.isIntersecting ? tryPlay(en.target) : en.target.pause()));
      }, { threshold: 0.15 });
    }
    scope.querySelectorAll('video.jm-autovideo').forEach((v) => {
      if (v._jm) return;
      v._jm = true;
      v.muted = true;
      v.defaultMuted = true;
      v.loop = true;
      v.playsInline = true;
      v.controls = false;
      v.removeAttribute('controls');
      v.setAttribute('muted', '');
      v.setAttribute('playsinline', '');
      v.setAttribute('disablepictureinpicture', '');
      v.setAttribute('disableremoteplayback', '');
      if (videoObserver) videoObserver.observe(v);
      else tryPlay(v);
    });
  }

  /* ───────── Dialogs + shipping protection (delegated) ───────── */
  document.addEventListener('click', async (e) => {
    const opener = e.target.closest('[data-jm-open]');
    if (opener) {
      const dlg = document.getElementById(opener.dataset.jmOpen);
      if (dlg && typeof dlg.showModal === 'function') { e.preventDefault(); dlg.showModal(); }
      return;
    }
    const closer = e.target.closest('[data-jm-close]');
    if (closer) {
      const dlg = closer.closest('dialog');
      if (dlg) dlg.close();
      return;
    }
    if (e.target.tagName === 'DIALOG' && e.target.classList.contains('jm-modal')) {
      e.target.close();
      return;
    }
    const protect = e.target.closest('[data-jm-protect-add]');
    if (protect) {
      e.preventDefault();
      protect.disabled = true;
      try {
        const selectId = protect.dataset.jmVariantSelect;
        const select = selectId && document.getElementById(selectId);
        const id = Number(select ? select.value : protect.dataset.jmProtectAdd);
        const data = await addToCart([{ id, quantity: 1 }]);
        if (protect.closest('cart-drawer')) showCart(data);
        else window.location.reload();
      } catch (err) {
        protect.disabled = false;
      }
    }
  });

  /* ───────── Discount code field (cart) ───────── */
  document.addEventListener('click', (e) => {
    const apply = e.target.closest('[data-jm-discount-apply]');
    if (apply) applyDiscount(apply.closest('[data-jm-discount]'));
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && e.target.matches('[data-jm-discount-input]')) {
      e.preventDefault();
      applyDiscount(e.target.closest('[data-jm-discount]'));
    }
  });
  async function applyDiscount(box) {
    if (!box) return;
    const input = box.querySelector('[data-jm-discount-input]');
    const err = box.querySelector('[data-jm-discount-error]');
    const code = (input.value || '').trim();
    if (!code) return;
    err.hidden = true;
    const existing = (box.dataset.applied || '').split(',').map((c) => c.trim()).filter(Boolean);
    const all = existing.concat(code).join(',');
    const drawer = getDrawer();
    try {
      const res = await fetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: all, sections: drawer ? ['cart-drawer', 'cart-icon-bubble'] : undefined })
      });
      const data = await res.json();
      const hit = (data.discount_codes || []).find((d) => d.code.toLowerCase() === code.toLowerCase());
      if (!hit || hit.applicable === false) {
        err.hidden = false;
        await fetch(root + 'cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ discount: existing.join(',') }) });
        return;
      }
      if (drawer && data.sections) showCart(data);
      else window.location.reload();
    } catch (x) {
      err.hidden = false;
    }
  }

  /* ───────── Gift message (saved as the cart note) ───────── */
  let giftTimer;
  document.addEventListener('input', (e) => {
    const field = e.target.closest('[data-jm-gift]');
    if (!field) return;
    const status = field.parentElement.querySelector('[data-jm-gift-status]');
    if (status) status.hidden = true;
    clearTimeout(giftTimer);
    giftTimer = setTimeout(async () => {
      try {
        const res = await fetch(root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note: field.value })
        });
        if (res.ok && status) status.hidden = false;
      } catch (err) {
        /* next keystroke retries */
      }
    }, 600);
  });

  /* ───────── Promo pop-up: exit intent on desktop (ShrinePro handles the timed open) ───────── */
  function initExitIntent() {
    const modal = document.querySelector('promo-popup .sign-up-popup-modal');
    const overlay = document.querySelector('promo-popup .sign-up-popup-overlay');
    if (!modal || !overlay) return;
    const days = Number(modal.closest('promo-popup').dataset.delayDays) || 7;
    const key = 'jm-popup-seen';
    const seenRecently = () => {
      try { return Date.now() - Number(localStorage.getItem(key) || 0) < days * 864e5; } catch (e) { return true; }
    };
    const mark = () => { try { localStorage.setItem(key, String(Date.now())); } catch (e) { /* private mode */ } };
    new MutationObserver(() => { if (modal.classList.contains('popup-modal--active')) mark(); })
      .observe(modal, { attributes: true, attributeFilter: ['class'] });
    if (!window.matchMedia('(pointer: fine)').matches) return;
    document.addEventListener('mouseout', (e) => {
      if (e.relatedTarget || e.clientY > 8 || seenRecently()) return;
      if (modal.classList.contains('popup-modal--active')) return;
      overlay.classList.add('popup-overlay--active');
      modal.classList.add('popup-modal--active');
      mark();
    });
  }

  /* ───────── Snowfall (optional) ───────── */
  function initSnow() {
    if (document.body.dataset.jmSnow !== 'true' || reduceMotion.matches || document.querySelector('.jm-snow')) return;
    const canvas = document.createElement('canvas');
    canvas.className = 'jm-snow';
    canvas.setAttribute('aria-hidden', 'true');
    document.body.appendChild(canvas);
    const ctx = canvas.getContext('2d');
    const count = Math.min(80, parseInt(document.body.dataset.jmSnowAmount, 10) || 30);
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    let w = 0;
    let h = 0;
    let running = true;
    const resize = () => {
      w = window.innerWidth; h = window.innerHeight;
      canvas.width = w * dpr; canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    const make = (top) => ({ x: Math.random() * w, y: top ? -10 : Math.random() * h, r: 1 + Math.random() * 2, s: 0.2 + Math.random() * 0.45, d: Math.random() * 6.28, a: 0.5 + Math.random() * 0.4 });
    resize();
    const flakes = Array.from({ length: count }, () => make(false));
    window.addEventListener('resize', resize, { passive: true });
    document.addEventListener('visibilitychange', () => { running = !document.hidden; if (running) requestAnimationFrame(tick); });
    function tick() {
      if (!running) return;
      ctx.clearRect(0, 0, w, h);
      for (const f of flakes) {
        f.y += f.s; f.d += 0.01; f.x += Math.sin(f.d) * 0.35;
        if (f.y > h + 10) Object.assign(f, make(true));
        ctx.beginPath();
        ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${f.a})`;
        ctx.shadowColor = 'rgba(23,58,46,0.35)';
        ctx.shadowBlur = 3;
        ctx.fill();
      }
      requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  /* ───────── Boot ───────── */
  function boot(scope) {
    scope.querySelectorAll('[data-jm-gallery]').forEach(initGallery);
    scope.querySelectorAll('[data-jm-minirev]').forEach(initMinirev);
    scope.querySelectorAll('[data-jm-slider]').forEach(initSlider);
    initVideos(scope);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { boot(document); initSnow(); initExitIntent(); hideCutoffOnly(); });
  } else {
    boot(document);
    initSnow();
    initExitIntent();
    hideCutoffOnly();
  }
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
  /* jm-drawer-editor: the cart drawer stays off-canvas; open it only while its section is selected in the editor */
  document.addEventListener('shopify:section:select', (e) => {
    if (!/cart-drawer/.test(e.target.id || '')) return;
    const d = getDrawer();
    if (d && typeof d.open === 'function') d.open();
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    if (!/cart-drawer/.test(e.target.id || '')) return;
    const d = getDrawer();
    if (d && typeof d.close === 'function') d.close();
  });
})();
